import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useShop, api } from "@/store";
import { M, J } from "@/data/catalogue";
import PageHd from "@/components/PageHd";
import { Reveal, CountUp, Bar } from "@/components/motion";

export default function Impact() {
  const { t, L, money } = useShop();
  const s = (k) => t(`impact.${k}`);
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    try {
      const r = await api(`/verify-bag?code=${encodeURIComponent(code.trim())}`);
      setRes(r);
      setErr(false);
    } catch {
      setRes(null);
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHd eb={s("eb")} h={s("h")} p={s("p")} testid="impact-title" />
      <div className="wrap" style={{ paddingBottom: 60 }}>
        <div className="tiles" data-testid="impact-tiles">
          {s("tiles").map((x, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="tile" style={{ height: "100%" }}>
                <CountUp to={x[0]} suffix={x[1]} />
                <span>{x[2]}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid g2" style={{ marginTop: 56, alignItems: "start" }}>
          <Reveal>
            <div>
              <div className="eyebrow">{s("split")}</div>
              <div className="bars" style={{ marginTop: 20 }} data-testid="ledger-bars">
                {s("rows").map((r, i) => (
                  <Bar key={r[0]} label={r[0]} value={money(r[1])} pct={(r[1] / 295) * 100} delay={i * 0.06} />
                ))}
              </div>
              <div className="eyebrow" style={{ marginTop: 44 }}>{s("cmp")}</div>
              <div className="bars" style={{ marginTop: 20 }}>
                {s("cmpr").map((r, i) => (
                  <Bar key={r[0]} label={r[0]} value={money(r[1])} pct={(r[1] / 130) * 100} tone={i ? "" : "teal"} delay={i * 0.1} />
                ))}
              </div>
              <p className="note">{s("dis")}</p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div>
              <div className="im" style={{ aspectRatio: "4/3", overflow: "hidden", background: "var(--bg-3)" }}>
                <img src={M.rancheria} alt="Weaving circle" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ marginTop: 34 }} id="verify">
                <div className="eyebrow">{s("verify")}</div>
                <p className="lede" style={{ margin: "10px 0 16px", fontSize: 16 }}>{s("verifyT")}</p>
                <form className="lookup" onSubmit={verify} data-testid="verify-form">
                  <input
                    data-testid="verify-bag-input"
                    placeholder={s("ph")}
                    aria-label={s("verify")}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <button className="btn primary" type="submit" data-testid="verify-bag-submit" disabled={busy}>
                    {busy ? <span className="spin" /> : s("go")}
                  </button>
                </form>
                {res && (
                  <div className="result" data-testid="verify-result">
                    <div className="ok"><Check size={20} /></div>
                    <dl>
                      <dt>{s("ok")}</dt>
                      <dd><strong style={{ fontWeight: 500 }}>{res.name}</strong> · {res.pattern}</dd>
                      <dt>{t("pdp.by")}</dt>
                      <dd>{res.artisan} · {res.place}, La Guajira</dd>
                      <dt>{t("pdp.days")}</dt>
                      <dd>{res.days}</dd>
                      <dt>{s("code")}</dt>
                      <dd><code>{res.code}</code></dd>
                    </dl>
                  </div>
                )}
                {err && <p className="note" data-testid="verify-error" style={{ borderColor: "var(--bad)" }}>{s("bad")}</p>}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <section className="sec">
        <div className="wrap split">
          <Reveal><div className="im"><img src={M.cabo} alt="Cabo de la Vela at dusk" loading="lazy" /></div></Reveal>
          <Reveal delay={0.12}>
            <div className="eyebrow">{t("journal.eb")}</div>
            <h2 style={{ fontSize: "clamp(30px,3.6vw,46px)" }}>{L(J[2]).t}</h2>
            <p className="lede">{L(J[2]).s}</p>
            <div style={{ marginTop: 22 }}>
              <Link className="btn ghost" to="/journal/pay">{t("journal.read")} →</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
